import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

// Types
interface Thumbnail {
  id: string;
  src: string;
  videoId: string;
}

interface VideoThumbnailsProps {
  thumbnails: Thumbnail[];
  videoId: string;
  onThumbnailClick?: (thumbnail: Thumbnail) => void;
}

// Styled Components
const ThumbnailsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px;
`;

const VideoSection = styled.div`
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const ThumbnailsRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  overflow-x: hidden;
  position: relative;
  width: 100%;

  &:first-child {
    border-top-left-radius: 15px !important;
    border-bottom-left-radius: 15px;
  }

  &:last-child {
    border-top-right-radius: 15px !important;
    border-bottom-right-radius: 15px;
  }
`;

const  ThumbnailColumn= styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content:center;
    width:100%;
    gap: 0px;
`
const ThumbnailWrapper = styled.div`

    height: 64px;
    width: 64px;
    flex: 0 0 auto;
    cursor: pointer;
    transition: transform 0.2s;
    
    &:hover {
        transform: scale(1.05);
    }
`;

const ThumbnailImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
`;

const HiddenIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 68px;
  background-color: rgba(0, 0, 0, 0.1);
  color: white;
  border-radius: 4px;
  font-size: 20px;
  font-weight: bold;
`;

const ThumbnailNavigationControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

const NavigationDot = styled.div<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#007bff' : '#ccc'};
  cursor: pointer;
  transition: background-color 0.2s;
`;


const VideoThumbnails: React.FC<VideoThumbnailsProps> = ({ thumbnails, videoId, onThumbnailClick }) => {
  const [visibleThumbnailsCount, setVisibleThumbnailsCount] = useState(10);
  const [activePage, setActivePage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  

  const videoThumbnails = thumbnails.filter(thumb => thumb.videoId === videoId);
  const totalThumbnails = videoThumbnails.length;
  

  const maxThumbnailsPerPage = visibleThumbnailsCount;
  const totalPages = Math.ceil(totalThumbnails / maxThumbnailsPerPage);
  

  const startIdx = activePage * maxThumbnailsPerPage;
  const endIdx = Math.min(startIdx + maxThumbnailsPerPage, totalThumbnails);
  

  const leftSideCount = Math.ceil(visibleThumbnailsCount / 2);
  const rightSideCount = Math.floor(visibleThumbnailsCount / 2);
  
  const leftThumbnails = videoThumbnails.slice(startIdx, startIdx + leftSideCount);
  const rightThumbnails = videoThumbnails.slice(
    startIdx + leftSideCount, 
    startIdx + leftSideCount + rightSideCount
  );
  
  
  const hasHiddenThumbnails = totalThumbnails > visibleThumbnailsCount;
  
  
  const hiddenThumbnailsCount = totalThumbnails - visibleThumbnailsCount;
  
  // Update visible thumbnails count based on container width
  useEffect(() => {
    const updateVisibleCount = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Assuming each thumbnail + gap is around 128px (64px + 8px)
        const thumbnailsPerRow = Math.floor(containerWidth / 64);
        // Always ensure even number for equal left/right split
        const adjustedCount = Math.max(2, thumbnailsPerRow - (thumbnailsPerRow % 2));
        setVisibleThumbnailsCount(Math.min(adjustedCount, 10));
      }
    };
    
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    
    return () => {
      window.removeEventListener('resize', updateVisibleCount);
    };
  }, []);
  
  return (
    <VideoSection>
      <ThumbnailsContainer ref={containerRef}>
        <ThumbnailsRow>
          {/* Left side thumbnails */}
          <ThumbnailColumn>
            {videoThumbnails.map((thumbnail) => (
                <ThumbnailWrapper 
                key={thumbnail.id}
                onClick={() => onThumbnailClick && onThumbnailClick(thumbnail)}
                >
                <ThumbnailImage src={thumbnail.src} alt="Video thumbnail" />
                </ThumbnailWrapper>
            ))}
          </ThumbnailColumn>
          
          
         
        </ThumbnailsRow>
        
        {/* Navigation dots for multiple pages
        {totalPages > 1 && (
          <ThumbnailNavigationControls>
            {Array.from({ length: totalPages }).map((_, index) => (
              <NavigationDot 
                key={index}
                active={index === activePage}
                onClick={() => setActivePage(index)}
                title={`Page ${index + 1}`}
              />
            ))}
          </ThumbnailNavigationControls>
        )} */}
      </ThumbnailsContainer>
    </VideoSection>
  );
};

export default VideoThumbnails;